import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { Button, ErrorNote } from "@/components/ui";
import { Labeled, Select, TextArea, TextField } from "@/components/Form";
import { CustomerPicker } from "@/components/CustomerPicker";
import { LineFields, lineTotals } from "@/components/LineFields";
import { SectionHeader } from "@/components/FormSection";
import { IconTrash, IconUser } from "@/components/icons";
import { tServiceKind } from "@/i18n";
import {
  emptyLineDraft,
  lineDraftIsValid,
  lineDraftToPayload,
  withCustomerName,
  type LineDraft,
} from "@/lib/bookingLines";
import { BookingAgentFields } from "@/components/BookingAgentFields";
import {
  agentDraftIsValid,
  agentDraftIssues,
  agentDraftToPayload,
  emptyAgentDraft,
  type AgentDraft,
} from "@/lib/bookingAgent";
import { useAuth } from "@/auth/auth";
import type { Booking } from "@/lib/types";

const CURRENCIES = ["AED", "USD", "EUR", "RUB", "SAR"];

export default function QuickCreateBookingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();
  // The agent's commission is money: only for those who may write finance.
  const canAgent = can("finance:write");
  const [agent, setAgent] = useState<AgentDraft>(emptyAgentDraft());
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [currency, setCurrency] = useState("AED");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLineDraft(currency)]);
  const create = useEntityAction([["bookings"]], { inlineError: true });

  const setLine = (i: number, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, emptyLineDraft(currency, customerName)]);
  // A line's tourist name follows the order's customer until it is typed over.
  const changeCustomerName = (name: string) => {
    setCustomerName(name);
    setLines((prev) => prev.map((l) => withCustomerName(l, name)));
  };
  const removeLine = (i: number) =>
    setLines((prev) => prev.filter((_, idx) => idx !== i));

  const customerMissing = !customerId && customerName.trim() === "";
  const agentIssues = canAgent ? agentDraftIssues(agent) : { agent: false, value: false };
  const valid =
    !customerMissing &&
    lines.length > 0 &&
    lines.every(lineDraftIsValid) &&
    (!canAgent || agentDraftIsValid(agent));
  // What is still blank, named for the message under the Create button.
  const missing = [
    customerMissing ? t("fields.customer") : null,
    lines.some((l) => l.service_date.trim() === "") ? t("bookingLines.serviceDate") : null,
    agentIssues.agent ? t("bookingAgent.agent") : null,
    agentIssues.value ? t("bookingAgent.commission") : null,
  ].filter(Boolean) as string[];
  const [tried, setTried] = useState(false);

  const submit = () => {
    if (!valid) {
      // Not a dead, disabled button: say what is missing, the fields glow.
      setTried(true);
      return;
    }
    const agentPayload = canAgent ? agentDraftToPayload(agent) : null;
    create.mutate(
      () =>
        api.post<Booking>("/bookings/quick", {
          customer_name: customerName || null,
          customer_id: customerId,
          currency,
          source: agentPayload ? "travel_agent" : "whatsapp",
          agent: agentPayload,
          notes: notes || null,
          lines: lines.map((l) => lineDraftToPayload(l, currency)),
        }),
      { onSuccess: (b) => navigate(`/bookings/${(b as Booking).booking_number}`) },
    );
  };

  // The bar's summary over every line (AED preview, like each line's own figures).
  const totals = lines.map((l) => lineTotals(l, t));
  const saleTotal = totals.reduce((sum, f) => sum + (f.converted ? f.sale : 0), 0);
  const marginTotal = totals.reduce((sum, f) => sum + (f.converted ? f.margin : 0), 0);
  const unconverted = [
    ...new Set(lines.filter((_, i) => !totals[i]!.converted).map((l) => l.currency)),
  ];

  return (
    <div className="nm-form space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/bookings" className="text-sm text-sand-muted hover:text-gold">
          {t("bookings.back")}
        </Link>
        <h1 className="text-lg font-semibold">{t("bookings.quickCreateTitle")}</h1>
      </div>

      <section className="nm-card p-5 sm:p-6">
        <SectionHeader
          icon={<IconUser size={16} />}
          tone="teal"
          title={t("quickCreate.client")}
          hint={t("quickCreate.clientHint")}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <Labeled
            label={t("fields.customerRecord")}
            required
            invalid={customerMissing}
            error={t("bookings.customerRequired")}
          >
            <CustomerPicker
              value={customerId}
              onChange={(id, label) => {
                setCustomerId(id);
                if (label) changeCustomerName(label);
              }}
            />
          </Labeled>
          <Labeled label={t("bookings.customerNameManual")} required invalid={customerMissing}>
            <TextField
              value={customerName}
              onChange={(e) => changeCustomerName(e.target.value)}
            />
          </Labeled>
          <Labeled label={t("fields.currency")}>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </Labeled>
          {canAgent && (
            <BookingAgentFields
              value={agent}
              onChange={(patch) => setAgent((prev) => ({ ...prev, ...patch }))}
              currency={currency}
              tried
            />
          )}
        </div>
        <div className="mt-4">
          <Labeled label={t("fields.notes")}>
            <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Labeled>
        </div>
      </section>

      {lines.map((line, i) => (
        <section key={i} className="nm-card p-5 sm:p-6" aria-label={`${t("quickCreate.line")} ${i + 1}`}>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-[#E0F2EF] px-3 py-1 text-sm font-bold text-[#1E6B65]">
              {t("quickCreate.line")} {i + 1} · {tServiceKind(line.service_kind)}
            </span>
            {lines.length > 1 && (
              <button
                type="button"
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-[#FEECEC] px-3 py-1.5 text-xs font-semibold text-[#B42318] hover:bg-[#FDDCDC]"
                onClick={() => removeLine(i)}
              >
                <IconTrash size={14} />
                {t("quickCreate.removeLine")}
              </button>
            )}
          </div>
          <LineFields
            value={line}
            onChange={(patch) => setLine(i, patch)}
            showDriver={false}
            grouped
          />
        </section>
      ))}

      <button
        type="button"
        onClick={addLine}
        className="flex w-full items-center justify-center rounded-card border-[1.5px] border-dashed border-[#2A9D8F] bg-[#F3FAF8] px-4 py-4 text-sm font-bold text-[#1E6B65] transition hover:bg-[#E0F2EF]"
      >
        + {t("bookingLines.add")}
      </button>

      <div
        className="sticky bottom-0 z-20 -mx-4 border-t border-[#A8DDD5] bg-surface px-4 pt-3 shadow-[0_-6px_20px_rgba(16,60,56,0.10)] nav:-mx-8 nav:px-8"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {tried && !valid && missing.length > 0 && (
          <p role="alert" className="mb-2 text-sm font-medium text-amber-700">
            {t("common.fillRequired")}: {missing.join(", ")}
          </p>
        )}
        {create.isError && (
          <div className="mb-2">
            <ErrorNote message={(create.error as Error).message} />
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <p className="min-w-0 text-sm text-ink" data-testid="booking-summary">
            {t("quickCreate.summaryLines", { count: lines.length })}
            {" · "}
            {t("bookingLines.previewSale")}: <b>{(saleTotal / 100).toFixed(0)} AED</b>
            {" · "}
            {t("bookingLines.margin")}:{" "}
            <b className={marginTotal < 0 ? "text-[#B42318]" : marginTotal > 0 ? "text-[#166534]" : ""}>
              {(marginTotal / 100).toFixed(0)} AED
            </b>
            {unconverted.length > 0 && (
              <span className="text-ink-muted">
                {" · "}
                {t("quickCreate.summaryNotConverted", { list: unconverted.join(", ") })}
              </span>
            )}
          </p>
          <div className="ml-auto flex gap-2">
            <Link
              to="/bookings"
              className="inline-flex h-11 items-center rounded-xl border border-[#1E6B65] bg-surface px-4 text-sm font-semibold text-[#1E6B65] hover:bg-[#E0F2EF]"
            >
              {t("common.cancel")}
            </Link>
            <Button
              loading={create.isPending}
              onClick={submit}
              className="h-11 rounded-xl px-6 text-base"
            >
              {t("quickCreate.createBooking")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
